import { useAppSharedURL, useAppDeployShare, useAppDeployUnshare } from "@/api";
import { TypographyMuted } from "@/components/typography/Muted";
import { TypographySM } from "@/components/typography/SM";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copyButton";
import Dialog from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { DialogClose } from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { Dispatch, SetStateAction } from "react";
import { PartialApp } from "../../types/app.type";

interface Props {
  app: PartialApp;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export function ShareApp({ app, setOpen }: Props) {
  const { data: sharedURL } = useAppSharedURL(app.slug);
  const [appDeployShare] = useAppDeployShare({
    deployID: app.defaultDeployment?.id || "",
    appID: app.id
  });
  const [appDeployUnshare] = useAppDeployUnshare({
    deployID: app.defaultDeployment?.id || "",
    appID: app.id
  });

  function handleToggleShare() {
    if (!sharedURL)
      appDeployShare().catch((error) => {
        console.error(error);
        toast({
          title: "Unable to enable share link",
          description:
            "An error occurred while activating the share link. Please try again or report the issue."
        });
      });
    else
      appDeployUnshare().catch((error) => {
        console.error(error);
        toast({
          title: "Unable to disable share link",
          description:
            "An error occurred while disabling the share link. Please try again or report the issue."
        });
      });
  }

  return (
    <Dialog.Root open onOpenChange={setOpen}>
      <Dialog.Content className="dark:bg-brand-gray-900">
        <Dialog.Header className="space-y-2 relative">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100 ">
            Share app
          </Dialog.Title>
          <div className="text-brand-gray-800 dark:text-brand-gray-100">
            <TypographyMuted>
              The share link allows anyone to access your app via shared URL. If
              you would like to have more control over who has access to your
              app, we recommend offering a free or paid subscription to your app
              instead.
            </TypographyMuted>
            <Separator className="my-4" />
            <div className="flex justify-between mb-4">
              <TypographySM>
                Enable share link access to{" "}
                <span className="font-medium dark:text-white text-brand-gray-950">
                  {app.displayName}
                </span>
              </TypographySM>
              <Switch
                checked={!!sharedURL}
                onCheckedChange={handleToggleShare}
              />
            </div>
            <AnimatePresence initial={false}>
              {sharedURL && (
                <motion.div
                  className="flex gap-2 overflow-hidden"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                >
                  <CopyButton
                    className="shrink-0"
                    variant="outline"
                    copyText={sharedURL}
                  />
                  <Input
                    className="cursor-default pointer-events-none"
                    value={sharedURL}
                    readOnly
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Dialog.Header>

        <Dialog.Footer className="mt-4">
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Close
            </Button>
          </DialogClose>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
